import type {
  PageParams as BasePageParams,
  TrackParams as BaseTrackParams,
  IdentifyParams,
} from '@segment/analytics-node';
import type { ClientInfo } from './client-info';

const MESSAGE_PREFIX = 'Bolt';

export enum AnalyticsTrackEvent {
  MessageSent = `${MESSAGE_PREFIX} Message sent`,
  MessageComplete = `${MESSAGE_PREFIX} Message complete`,
  MessageCompleteError = `${MESSAGE_PREFIX} Message complete Error`,
  ErrorReasoningComplete = `${MESSAGE_PREFIX} Error reasoning complete`,
  ErrorReasoningFailed = `${MESSAGE_PREFIX} Error reasoning failed`,
  ClassifyAttachmentsComplete = `${MESSAGE_PREFIX} Classify attachments complete`,
  ClassifyAttachmentsFailed = `${MESSAGE_PREFIX} Classify attachments failed`,
  ChatCreated = `${MESSAGE_PREFIX} Chat created`,
  MessageTokenLimt = `${MESSAGE_PREFIX} Message token limit`,
  BoltConcurrentFreeUsers = `${MESSAGE_PREFIX} Concurrent free users limit`,
  BoltConcurrentFreeUsersRetry = `${MESSAGE_PREFIX} Concurrent free users limit retry`,
  APIForbidden = `${MESSAGE_PREFIX} API forbidden`,
  AnthropicCountTokensFailed = `${MESSAGE_PREFIX} Anthropic count tokens failed`,
  AnthropicAPIFailure = `${MESSAGE_PREFIX} Anthropic API failure`,
  AnthropicAPIRateLimit = `${MESSAGE_PREFIX} Anthropic API rate limit`,
  AnthropicAPIOverloaded = `${MESSAGE_PREFIX} Anthropic API overloaded`,
  AnthropicAPIRequestTooLarge = `${MESSAGE_PREFIX} Anthropic API request too large`,
  AnthropicAPICreditBalanceLow = `${MESSAGE_PREFIX} Anthropic API credit balance low`,
  SiteDeployDialogOpen = `${MESSAGE_PREFIX} Site deploy dialog open`,
  AnthropicUnknownToolCall = `${MESSAGE_PREFIX} Anthropic unknown tool call`,
  AnthropicMaxContextSizeReached = `${MESSAGE_PREFIX} Anthropic max context size reached`,
  AnthropicMaxImageSizeExceeded = `${MESSAGE_PREFIX} Anthropic max image size exceeded`,
  AnthropicUserCancellation = `${MESSAGE_PREFIX} Anthropic user cancellation`,
  AnthropicAISDKError = `${MESSAGE_PREFIX} Anthropic AI SDK error`,
  SiteDeploySuccess = `${MESSAGE_PREFIX} Site deploy success`,
  SiteDeployFailure = `${MESSAGE_PREFIX} Site deploy failure`,
  SiteNetlifyLogsFailure = `${MESSAGE_PREFIX} Site Netlify logs failure`,
  SiteBuildFailed = `${MESSAGE_PREFIX} Site build failure`,
  DailyTokenLimitReached = `${MESSAGE_PREFIX} Daily token limit reached`,
  MonthlyTokenLimitReached = `${MESSAGE_PREFIX} Monthly token limit reached`,
  PercentageOfTokensUsed = `${MESSAGE_PREFIX} Percentage of tokens used`,
  TemplateUsed = `${MESSAGE_PREFIX} Template used`,
  NoTemplateUsed = `${MESSAGE_PREFIX} No template used`,
  RailsApiCallFailed = `${MESSAGE_PREFIX} Rails api call failed`,
  MessageStreamStarted = `${MESSAGE_PREFIX} Message stream started`,
  ChatCancelled = `${MESSAGE_PREFIX} Chat cancelled by user`,
  ChatError = `${MESSAGE_PREFIX} Chat stopped due to an error`,
  GrowSurfFailFetchReferrals = `${MESSAGE_PREFIX} Growsurf Failed Fetch Referrals`,
  RequestPerChatId = `${MESSAGE_PREFIX} Request id mapped to chat id`,
  InvalidHunk = `${MESSAGE_PREFIX} Invalid hunk`,
  FixingFile = `${MESSAGE_PREFIX} Fixing file`,
  DiffFinishedStreaming = `${MESSAGE_PREFIX} Diff finished streaming`,
  LazinessPresent = `${MESSAGE_PREFIX} Laziness present`,
  TokenRequestUpdateFailed = `${MESSAGE_PREFIX} Token request update failed`,
  CallDeployLLM = `${MESSAGE_PREFIX} Call deploy fast llm`,
  CallDeployZero = `${MESSAGE_PREFIX} Call deploy zero`,
  ErrorQueueSendFailed = `${MESSAGE_PREFIX} Error queue send failed`,
  OutOfMemory = `${MESSAGE_PREFIX} Out of memory`,
  WebContainerStartupError = `${MESSAGE_PREFIX} WebContainer startup error`,
  WebContainerStartupTimeout = `${MESSAGE_PREFIX} WebContainer startup timeout`,
  RateLimitUpdateFailed = `${MESSAGE_PREFIX} Rate limit update failed`,
  RateLimitUpdateDebug = `${MESSAGE_PREFIX} Rate limit update debug`,
  RateLimitForUnknownMessage = `${MESSAGE_PREFIX} Rate limit for unknown message`,
  DeprecatedDeployInfoUsed = `${MESSAGE_PREFIX} Deprecated deploy info used`,
  DBMigrationWithTransaction = `${MESSAGE_PREFIX} DB Migration with transaction`,
  UpdateSupabaseMigrationTableFailed = `${MESSAGE_PREFIX} Update supabase migration table failed`,
  UserTokenReset = `${MESSAGE_PREFIX} User token reset`,
  OrganizationMemberTokenReset = `${MESSAGE_PREFIX} Organization member token reset`,
  SupabaseConnectionFailed = `${MESSAGE_PREFIX} Error setting up supabase`,
  SupabaseConnectionSuccess = `${MESSAGE_PREFIX} Success setting up supabase`,
  SupabaseTokenRefreshFailed = `${MESSAGE_PREFIX} Failed to refresh Supabase token`,
  SupabaseApiTokenRevoked = `${MESSAGE_PREFIX} Supabase API token revoked`,
  SupabaseFetchApiTokenFailed = `${MESSAGE_PREFIX} Failed to fetch Supabase API token`,
  SupabaseRefreshContextFailed = `${MESSAGE_PREFIX} Refreshing supabase context failed`,
  SupabaseRefreshContextSuccess = `${MESSAGE_PREFIX} Refreshing supabase context success`,
  RailsUpdateThirdPartyTokenFailed = `${MESSAGE_PREFIX} Failed to update third party tokens in rails`,
  RailsFetchThirdPartyTokenFailed = `${MESSAGE_PREFIX} Failed to fetch third party tokens in rails`,
  RailsFetchOneTimeTokenFailed = `${MESSAGE_PREFIX} Failed to fetch one time token in rails`,
  MissingExportUnsupportedFormat = `${MESSAGE_PREFIX} Missing export in svelte/vue`,
  InvalidBoltActionType = `${MESSAGE_PREFIX} Invalid BoltAction type`,
  NothingToDefaultExport = `${MESSAGE_PREFIX} Cannot find any identifiers to export as default`,
  BracketFixing = `${MESSAGE_PREFIX} Fixing brackets`,
  AnalysisDuration = `${MESSAGE_PREFIX} Analysis Duration`,
  WatcherExpectedEvents = `${MESSAGE_PREFIX} Watcher has not received all expected events`,
  ReferralTokensAdded = `${MESSAGE_PREFIX} Referral tokens added`,
  RepeatingUserError = `${MESSAGE_PREFIX} Repeating user error`,
  ProbablyFixedError = `${MESSAGE_PREFIX} Probably fixed an error`,
  AttemptFix = `${MESSAGE_PREFIX} Attempt Fix was clicked`,
  UserlandError = `${MESSAGE_PREFIX} Error was thrown in userland`,
  UseModel = `${MESSAGE_PREFIX} Use model`,
  CacheCreate = `${MESSAGE_PREFIX} Cache create`,
  FetchSchemaFromContextFailed = `${MESSAGE_PREFIX} Fetching schema from context failed`,
  StartFromFigmaFrame = `${MESSAGE_PREFIX} Start from Figma frame`,
  ConnectToFigma = `${MESSAGE_PREFIX} Connect to Figma`,
  ImportFigmaFrame = `${MESSAGE_PREFIX} Import Figma frame`,
  ImportedFigmaFrame = `${MESSAGE_PREFIX} Imported Figma frame`,
  FailedFigmaFrameImport = `${MESSAGE_PREFIX} Failed Figma frame import`,
  StripeWebhookCreationFailed = `${MESSAGE_PREFIX} Stripe Webhook Creation Failed`,
  StripeProductsSaveFailed = `${MESSAGE_PREFIX} Stripe Products persistence Failed`,
  StripeAPIKeySet = `${MESSAGE_PREFIX} Stripe API Key Set`,
  StripeInitialSetup = `${MESSAGE_PREFIX} Stripe Initial Setup`,
  StripeProductUpdated = `${MESSAGE_PREFIX} Stripe Product Updated`,
  StripeErrorDuringSetup = `${MESSAGE_PREFIX} Stripe Error During Setup`,
  EntriInitialSetup = `${MESSAGE_PREFIX} Entri Initial Setup`,
  EntriHyperlinksGenerated = `${MESSAGE_PREFIX} Entri Hyperlinks Generated`,
  EntriSellClicked = `${MESSAGE_PREFIX} Entri Sell Clicked`,
  EntriConnectClicked = `${MESSAGE_PREFIX} Entri Connect Clicked`,
  EdgeFunctionDeploySuccess = `${MESSAGE_PREFIX} Edge function deploy success`,
  EdgeFunctionDeployFailure = `${MESSAGE_PREFIX} Edge function deploy failure`,
  QuickActionUsed = `${MESSAGE_PREFIX} Quick action used`,
  UserRollbackToPreviousMessageStarted = `${MESSAGE_PREFIX} User rolled back to previous message started`,
  UserRollbackToPreviousMessageSuccess = `${MESSAGE_PREFIX} User rolled back to previous message success`,
  UserRollbackToPreviousMessageFailure = `${MESSAGE_PREFIX} User rolled back to previous message failure`,
  EnhancedPrompt = `${MESSAGE_PREFIX} Enhanced prompt success`,
  EnhancedPromptFailure = `${MESSAGE_PREFIX} Enhanced prompt failure`,
  EntriWebhookNetlifyFailure = `${MESSAGE_PREFIX} Entri Webhook Netlify failure`,
  EntriWebhookInvalidPayload = `${MESSAGE_PREFIX} Entri Webhook Invalid payload`,
  EntriWebhookSuccess = `${MESSAGE_PREFIX} Entri Webhook Success`,
  UsesInspectedElement = `${MESSAGE_PREFIX} Used inspected element`,
  DuplicateProjectSuccess = `${MESSAGE_PREFIX} Duplicate Project success`,
  DuplicateIntegrationsFailure = `${MESSAGE_PREFIX} Duplicate Integrations failure`,
  GithubRepoCreated = `${MESSAGE_PREFIX} User created GitHub repository`,
  GithubRepoCloneStarted = `${MESSAGE_PREFIX} User opened project linked to GitHub repository`,
  GithubRepoCloned = `${MESSAGE_PREFIX} User cloned a project linked to GitHub successfully`,
  GithubRepoCloneFailed = `${MESSAGE_PREFIX} User failed to clone project linked to GitHub`,
  GithubPull = `${MESSAGE_PREFIX} User pulled new changes from GitHub repository`,
  GithubRepoImportStarted = `${MESSAGE_PREFIX} User started importing GitHub repository`,
  GithubRepoImported = `${MESSAGE_PREFIX} User imported GitHub repository successfully`,
  GithubRepoImportFailed = `${MESSAGE_PREFIX} Failed GitHub repository import`,
  GithubBranchCreated = `${MESSAGE_PREFIX} User created GitHub branch`,
  GithubConflict = `${MESSAGE_PREFIX} Failed push to GitHub`,
  GithubConflictRetried = `${MESSAGE_PREFIX} User retried message after failed push`,
  GithubConflictDiscarded = `${MESSAGE_PREFIX} User discarded changes after failed push`,
  AnalyzeAttachmentsFailed = `${MESSAGE_PREFIX} Failed to analyze attachments`,
  StarterPillUsed = `${MESSAGE_PREFIX} Starter pill used`,
  PromptLibraryOpened = `${MESSAGE_PREFIX} Prompt Library opened`,
  PromptLibraryPromptUsed = `${MESSAGE_PREFIX} Prompt Library prompt used`,
  PromptLibraryPromptStarred = `${MESSAGE_PREFIX} Prompt Library prompt starred`,
  PromptLibraryPromptCreated = `${MESSAGE_PREFIX} Prompt Library prompt created`,
  PromptLibraryPromptUpdated = `${MESSAGE_PREFIX} Prompt Library prompt updated`,
  PromptLibraryCategoryCreated = `${MESSAGE_PREFIX} Prompt Library category created`,
  NpmInstallFailed = `${MESSAGE_PREFIX} Npm Install failed`,
  NpmInstallNeverRan = `${MESSAGE_PREFIX} Npm Install added but not run`,
  UserLoginCompleted = `${MESSAGE_PREFIX} User Login Completed`,
  UserLoginStarted = `${MESSAGE_PREFIX} User Login Started`,
  LoginDialogOpen = `${MESSAGE_PREFIX} Login Dialog Open`,
  LoginDialogClosed = `${MESSAGE_PREFIX} Login Dialog Closed`,
  NpmUpgrade = `${MESSAGE_PREFIX} NPM Upgrade`,
  NpmUpgradeNotPossible = `${MESSAGE_PREFIX} NPM Upgrade not possible`,
  EnterpriseContactUsClicked = `${MESSAGE_PREFIX} Enterprise Contact Us Clicked`,
  CompatibilityWarningDialogShown = `${MESSAGE_PREFIX} Compatibility Warning Dialog Shown`,
  DowngradeToFreePlanConfirmed = `${MESSAGE_PREFIX} Downgrade to Free Plan Confirmed`,
  ConfirmDowngradeToFreePlanCanceled = `${MESSAGE_PREFIX} Confirm Downgrade to Free Plan Canceled`,
  FailedToStoreDebugInfo = `${MESSAGE_PREFIX} Failed to store debug info`,
  TokensConsumed = `${MESSAGE_PREFIX} Tokens consumed`,
  RegistryAdded = `${MESSAGE_PREFIX} Registry added`,
  RegistryUpdated = `${MESSAGE_PREFIX} Registry updated`,
  RegistryDeleted = `${MESSAGE_PREFIX} Registry deleted`,
  TeamPromptUpdated = `${MESSAGE_PREFIX} Team prompt updated`,
  PackagePromptAdded = `${MESSAGE_PREFIX} Package prompt added`,
  PackagePromptUpdated = `${MESSAGE_PREFIX} Package prompt updated`,
  PackagePromptDeleted = `${MESSAGE_PREFIX} Package prompt deleted`,
  CreateTeamMenuClicked = `${MESSAGE_PREFIX} Create Team Clicked`,
  PricingPageOpened = `${MESSAGE_PREFIX} Pricing Page Opened`,
  CreateTeamModalOpened = `${MESSAGE_PREFIX} Create Team Modal Opened`,
  TeamSetupStarted = `${MESSAGE_PREFIX} Team Setup Started`,
  TeamProviderSelected = `${MESSAGE_PREFIX} Team Provider Selected`,
  TeamSlugEntered = `${MESSAGE_PREFIX} Team Slug Entered`,
  TeamCreated = `${MESSAGE_PREFIX} Team Created`,
  TeamCheckoutStarted = `${MESSAGE_PREFIX} Team Subscription Checkout Started`,
  TeamCheckoutCompleted = `${MESSAGE_PREFIX} Team Checkout Completed`,
  TeamSignupAbandoned = `${MESSAGE_PREFIX} Team Signup Abandoned`,
  TeamSettingsModalOpened = `${MESSAGE_PREFIX} Team Settings Modal Opened`,
  TokenSettingsModalOpened = `${MESSAGE_PREFIX} Token Settings Modal Opened`,
  TeamMembersChangeConfirmationModalOpened = `${MESSAGE_PREFIX} Team Members Change Confirmation Modal Opened`,
  AccountSelected = `${MESSAGE_PREFIX} Account Selected`,
  NetworkFailureRecovered = `${MESSAGE_PREFIX} Network Failure Recovered`,
  UpgradePlanClicked = `${MESSAGE_PREFIX} Upgrade Plan Clicked`,
  ManageSubscriptionClicked = `${MESSAGE_PREFIX} Manage Subscription Clicked`,
  PlanChangeStarted = `${MESSAGE_PREFIX} Plan Change Started`,
  BillingIntervalChanged = `${MESSAGE_PREFIX} Billing Interval Changed`,
  ProPlanUpgradeClicked = `${MESSAGE_PREFIX} Pro Plan Upgrade Clicked`,
  TeamPlanUpgradeClicked = `${MESSAGE_PREFIX} Team Plan Upgrade Clicked`,
  PlanCardClicked = `${MESSAGE_PREFIX} Plan Card Clicked`,
  CancelSubscriptionClicked = `${MESSAGE_PREFIX} Cancel Subscription Clicked`,
  SubscriptionCancellationConfirmed = `${MESSAGE_PREFIX} Subscription Cancellation Confirmed`,
  SubscriptionCancellationCanceled = `${MESSAGE_PREFIX} Subscription Cancellation Canceled`,
  PlanChangeFailed = `${MESSAGE_PREFIX} Plan Change Failed`,
  BillingPortalRedirectFailed = `${MESSAGE_PREFIX} Billing Portal Redirect Failed`,
  NetworkFailureRecovery = `${MESSAGE_PREFIX} Network Failure Recovery`,
  NetlifyDomainPurchased = `${MESSAGE_PREFIX} Netlify Domain Purchased`,
  MonthlySpendingLimitChanged = `${MESSAGE_PREFIX} Monthly Spending Limit Changed`,
  TeamMemberInvited = `${MESSAGE_PREFIX} Team Member Invited`,
  TeamMemberInvitationAccepted = `${MESSAGE_PREFIX} Team Member Invitation Accepted`,
  TeamMemberInvitationDeclined = `${MESSAGE_PREFIX} Team Member Invitation Declined`,
  TeamMemberInvitationResent = `${MESSAGE_PREFIX} Team Member Invitation Resent`,
  TeamMemberRoleChanged = `${MESSAGE_PREFIX} Team Member Role Changed`,
  TeamMemberUpgraded = `${MESSAGE_PREFIX} Team Member Upgraded`,
  TeamMemberDowngraded = `${MESSAGE_PREFIX} Team Member Downgraded`,
  TeamMemberRemoved = `${MESSAGE_PREFIX} Team Member Removed`,
  TeamMemberUpgradeRequested = `${MESSAGE_PREFIX} Team Member Upgrade Requested`,
  ClaudeCodeServerInvalidDiff = `${MESSAGE_PREFIX} Claude Code Server Invalid Diff`,
  ClaudeCodeTokenProcessingFailed = `${MESSAGE_PREFIX} Claude Code token processing failed`,
  ClaudeCodeServerSuccess = `${MESSAGE_PREFIX} Claude Code Server Success`,
  ClaudeCodeServerError = `${MESSAGE_PREFIX} Claude Code Server Error`,
  ClaudeCodeToolCallSuccess = `${MESSAGE_PREFIX} Tool Call Success`,
  ClaudeCodeToolCallFailure = `${MESSAGE_PREFIX} Tool Call Failure`,
  ClaudeCodeTokenUsageFailure = `${MESSAGE_PREFIX} Claude Code Token Usage Failure`,
  ClaudeCodeRefactorCode = `${MESSAGE_PREFIX} Claude Code Refactor Code`,
  VersionHistoryPanelOpened = `${MESSAGE_PREFIX} Version History panel opened`,
  VersionHistorySnapshotPreviewedFromChat = `${MESSAGE_PREFIX} Version History snapshot previewed from chat`,
  VersionHistorySnapshotPreviewedFromHistoryPanel = `${MESSAGE_PREFIX} Version History snapshot previewed from version history panel`,
  VersionHistoryPreviewExited = `${MESSAGE_PREFIX} Version History preview exited`,
  VersionHistoryBookmarkAdded = `${MESSAGE_PREFIX} Version History bookmark added`,
  VersionHistoryBookmarkRemoved = `${MESSAGE_PREFIX} Version History bookmark removed`,
  VersionHistorySnapshotRenamed = `${MESSAGE_PREFIX} Version History snapshot renamed`,
  VersionHistoryRestoreModalOpened = `${MESSAGE_PREFIX} Version History restore modal opened`,
  VersionHistoryRestoreModalCancelled = `${MESSAGE_PREFIX} Version History restore modal cancelled`,
  VersionHistoryRestoreStarted = `${MESSAGE_PREFIX} Version History restore started`,
  VersionHistoryRestoreSucceeded = `${MESSAGE_PREFIX} Version History restore succeeded`,
  VersionHistoryRestoreFailed = `${MESSAGE_PREFIX} Version History restore failed`,
  VersionHistoryHelpButtonClicked = `${MESSAGE_PREFIX} Version History help button clicked`,
  ProjectDownloaded = `${MESSAGE_PREFIX} Project downloaded as zip`,
  ProjectOpenedInStackBlitz = `${MESSAGE_PREFIX} Project opened in StackBlitz`,
  EndOfStreamNotFoundRecovered = `${MESSAGE_PREFIX} End of stream not found recovered`,
  DatabaseCtaClicked = `${MESSAGE_PREFIX} User clicks to add database`,
  DatabaseModalOpened = `${MESSAGE_PREFIX} Database setup modal shown`,
  DatabaseDeleted = `${MESSAGE_PREFIX} User deletes database along with project`,
  DatabaseDataQueried = `${MESSAGE_PREFIX} User clicks query the database`,
  SwitchAgent = `${MESSAGE_PREFIX} Switch Agent`,
}

export enum DatabaseTrigger {
  IntegrationsDropdown = 'Integrations Dropdown',
  Chat = 'Chat',
}

export enum AccountSelectedTrigger {
  PricingPage = 'Pricing Page',
  Menu = 'Menu',
}

export enum PricingPageOpenedTrigger {
  Menu = 'Menu',
  Header = 'Header',
  Footer = 'Footer',
  SettingsTokenTab = 'Settings Token Tab',
  SettingsTeamTab = 'Settings Team Tab',
  SettingsDatabaseTab = 'Settings Database Tab',
  UpgradePlanLink = 'Upgrade Plan Link',
  ReferralsModal = 'Referrals Modal',
  SettingsDeleteTeamModal = 'Settings Delete Team Modal',
  RateLimitModal = 'Rate Limit Modal',
  PaymentRenewalToast = 'Payment Renewal Toast',
  QueryParam = 'Query Parameter',
}

export enum CreateTeamTrigger {
  PlanCard = 'Bolt Plan Card',
  Menu = 'Menu',
  SettingsTeamTab = 'Settings Team Tab',
}

export enum AnalyticsAction {
  Identify = 'identify',
  Page = 'page',
  Track = 'track',
}

// useful to have a consistent status to use across events that are tracked to see if a given action went through or not
export enum AnalyticsEventStatus {
  Failure = 'failure',
  Success = 'success',
}

// we can omit the user ID since it's retrieved from the user's session
type OmitUserId<T> = Omit<T, 'userId'>;

export type BaseEventProperties = {
  environment?: string;
  agent?: string;
  [key: string]: any;
};

export type EventProperties = BaseEventProperties & {
  featurePreviews?: never;
  serverRevision?: never;
} & ClientInfo<never>;

export type PageParams = BasePageParams & { properties?: EventProperties };
export type TrackParams = BaseTrackParams & { properties?: EventProperties };

export type AnalyticsEvent =
  | { action: AnalyticsAction.Identify; payload: OmitUserId<IdentifyParams> }
  | { action: AnalyticsAction.Page; payload: OmitUserId<PageParams> }
  | { action: AnalyticsAction.Track; payload: OmitUserId<TrackParams>; isAnonymous?: boolean };
